import { GpuSystemsField } from 'app/views/home/webgl/shadersWebGL/gpu/GpuSystemsField';
import { getPixiGL, getRenderTextureTarget, GLStateGuard } from 'app/views/home/webgl/shadersWebGL/gpu/pixiGl';
import dev from 'config/dev';

import type { PixiGL } from 'app/views/home/webgl/shadersWebGL/gpu/pixiGl';
import type { ShadersSystemsConfig } from 'app/views/home/webgl/shadersWebGL/shadersSystemsConfig';
import type { DepartmentDeps, SystemsDepartmentLike } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

/**
 * GpuSystemsDepartment — the shaders type's REPLACEMENT for the CPU
 * SystemsDepartment (particlesOptimised). Same DepartmentScene surface, same
 * z-slot (deps.stage.systemsContainer), same per-frame scalars — but the
 * simulation AND the render run entirely on the GPU (GpuSystemsField), and the
 * output composites into the Pixi scene through a RenderTexture -> Sprite (the
 * interop resolved in gpu/pixiGl.ts). NOTHING is uploaded per particle per
 * frame — the CPU pack (PoolRenderer.upload) is gone by construction.
 *
 * STAGE 1 SCOPE (honest): this renders the GPU systems PARTICLE FIELD (the
 * grid-seek dots with eased drift — the proven spike workload) at the systems
 * position, moving with the drag and fading in with progress like the CPU
 * systems. It deliberately does NOT reproduce the CPU department's separate
 * CPU-Graphics layers — the static broken dot-grid guide (ParticleGridTargets)
 * and the big systems timeline items — which are unchanged chrome, not part of
 * the GPU port. Count matches the shipped 3650 (shadersSystemsConfig.count) for
 * a like-for-like; that number is the Stage-2 scaling-sweep hook.
 *
 * Positioning: the field is rendered in absolute screen space via render
 * uniforms (centre = containerX + systemsParticles.cx * virtualWidth, exactly
 * the CPU dept's _positionParticleContainer expression), so it tracks the drag
 * without relying on container translation; the systemsContainer stays at x=0
 * and only its facade-driven fade alpha (systemsFadeIn) is inherited by the
 * Sprite.
 */
export class GpuSystemsDepartment implements SystemsDepartmentLike {
	private _deps: DepartmentDeps;
	private _config: ShadersSystemsConfig;

	private _pixi: PixiGL | null = null;
	private _field: GpuSystemsField | null = null;
	private _guard: GLStateGuard | null = null;

	private _renderTexture: any = null; //PIXI.RenderTexture
	private _sprite: any = null; //PIXI.Sprite
	private _framebuffer: WebGLFramebuffer | null = null;
	private _fbPixelWidth = 0;
	private _fbPixelHeight = 0;

	private _rtLogicalWidth = 0;
	private _rtLogicalHeight = 0;

	private _active = false;
	private _time = 0;

	constructor(deps: DepartmentDeps, config: ShadersSystemsConfig) {
		this._deps = deps;
		this._config = config;
		//Keep the systems container hidden until the scene starts (mirrors the
		//CPU dept's setup — the facade fades its alpha over drag progress):
		this._deps.stage.systemsContainer.visible = false;
	}

	public get isActive(): boolean {
		return this._active;
	}

	//DepartmentScene surface
	//-----------------------------------------------------------------------------------------------

	/** Ticks EVERY frame (the facade's passive systems pass + the active pass),
	 *  exactly like the CPU systems dept — the GPU field tracks the drag whether
	 *  or not systems is the active scene. */
	public tick(): void {
		if (!this._ensureReady()) {
			return;
		}

		const sizes = this._deps.getSizes();
		if (!sizes || !sizes.zonePositions || !sizes.zonePositions.systemsParticles) {
			return;
		}

		this._syncRenderTextureSize(sizes.innerWidth, sizes.innerHeight);
		if (!this._framebuffer) {
			return;
		}

		//The CPU dept's _positionParticleContainer x, verbatim — the field
		//centre tracks the drag through containerX:
		const centreX = this._deps.context.containerX + sizes.zonePositions.systemsParticles.cx * sizes.virtualWidth;
		const centreY = sizes.innerHeight / 2;

		const deltaTime = this._deps.context.deltaTime || 1;
		this._time += 1;

		//One save/restore around both GPU passes — Pixi's state is untouched
		//on either side (Pixi re-binds its own targets each render anyway):
		this._guard.save();
		//?simSkip=1 (benchmark, dev-gated) freezes the compute pass so the same N
		//quads still rasterize — isolates fill-rate from compute (design §9):
		if (!dev.simSkip) {
			this._field.update(deltaTime, this._time);
		}
		this._field.render({
			framebuffer: this._framebuffer,
			pixelWidth: this._fbPixelWidth,
			pixelHeight: this._fbPixelHeight,
			centreX: centreX,
			centreY: centreY,
			halfWidth: sizes.innerWidth * this._config.fieldHalfWidthFactor,
			halfHeight: sizes.innerHeight * this._config.fieldHalfHeightFactor,
			viewportWidth: sizes.innerWidth,
			viewportHeight: sizes.innerHeight,
			resolution: this._pixi.renderer.resolution || 1
		});
		this._guard.restore();

		//NOTE: do NOT call renderTexture.source.update() here — it marks the RT
		//dirty and Pixi then re-renders (clears) it when compositing the Sprite,
		//wiping the field we just drew. The Sprite samples the RT's GL texture
		//live; we wrote that texture directly, so nothing more is needed.
	}

	public startAsActiveScene(): void {
		this._active = true;
		this._deps.stage.systemsContainer.visible = true;
	}

	public stopAsActiveScene(): void {
		this._active = false;
		this._deps.stage.systemsContainer.visible = false;
	}

	public startSceneDrag(): void {}

	public endSceneDrag(): void {}

	/** The RenderTexture is recreated at the new screen size on the next tick
	 *  (see _syncRenderTextureSize); nothing else to do — sim space is
	 *  normalised, so the field survives a resize untouched. */
	public resize(): void {}

	/** Never routed for systems (HomeField.setupForWork sends it to spaces). */
	public setupForWork(): void {}

	/** HomeField.showFromWork's else branch routes SYSTEMS here — re-show. */
	public showFromWork(): void {
		this.startAsActiveScene();
	}

	public fromWorkToHome(): void {}

	/** GPU systems has no CPU physics kernel (systems is off the shared
	 *  ParticleCore partition), so no lerp-completion callback ever fires. */
	public onPhysicsTargetReached(): void {}

	public destroy(): void {
		if (this._sprite) {
			this._sprite.parent?.removeChild(this._sprite);
			this._sprite.destroy();
			this._sprite = null;
		}
		if (this._renderTexture) {
			this._renderTexture.destroy(true);
			this._renderTexture = null;
		}
		if (this._field) {
			this._field.destroy();
			this._field = null;
		}
		this._framebuffer = null;
		this._pixi = null;
		this._guard = null;
	}

	//Private
	//-----------------------------------------------------------------------------------------------

	/** Lazy one-time GPU init — the Pixi WebGL2 context only exists once the
	 *  app has booted (after PixiController._createAppAsync), which is always
	 *  true by the first tick. Returns false (and no-ops) until then, or if the
	 *  context can't run RGBA32F ping-pong (design §10.1 capability fallback —
	 *  the shaders type simply shows no systems field rather than throwing). */
	private _ensureReady(): boolean {
		if (this._field) {
			return true;
		}
		const pixi = getPixiGL();
		if (!pixi) {
			return false;
		}
		if (!pixi.gl.getExtension('EXT_color_buffer_float')) {
			return false;
		}
		this._pixi = pixi;
		this._guard = new GLStateGuard(pixi.gl);
		this._field = new GpuSystemsField(pixi.gl, this._config.count, this._config.colour, this._config.pointSizePx);
		//Dev-only readback so the scaling harness can confirm the GPU field's own
		//buffer sized to the ?sysCount override (dev-gated — never in production):
		if (dev.active) {
			(window as { __SHADERS_SYS_COUNT__?: number }).__SHADERS_SYS_COUNT__ = this._field.count;
		}
		return true;
	}

	/** (Re)create the full-screen RenderTexture + its Sprite when the screen
	 *  size changes, and cache the GL framebuffer we draw the field into. */
	private _syncRenderTextureSize(logicalWidth: number, logicalHeight: number): void {
		if (!logicalWidth || !logicalHeight) {
			return;
		}
		if (this._renderTexture && this._rtLogicalWidth === logicalWidth && this._rtLogicalHeight === logicalHeight) {
			return;
		}

		const renderer = this._pixi.renderer;
		const resolution = renderer.resolution || 1;

		if (this._sprite) {
			this._sprite.parent?.removeChild(this._sprite);
			this._sprite.destroy();
			this._sprite = null;
		}
		if (this._renderTexture) {
			this._renderTexture.destroy(true);
			this._renderTexture = null;
		}

		this._renderTexture = (PIXI as any).RenderTexture.create({
			width: logicalWidth,
			height: logicalHeight,
			resolution: resolution
		});
		this._rtLogicalWidth = logicalWidth;
		this._rtLogicalHeight = logicalHeight;

		const target = getRenderTextureTarget(renderer, this._renderTexture);
		if (!target) {
			return;
		}
		this._framebuffer = target.framebuffer;
		this._fbPixelWidth = target.pixelWidth;
		this._fbPixelHeight = target.pixelHeight;

		//A plain Sprite over the RenderTexture, at screen origin, mounted in the
		//systems z-slot so Pixi composites it at the right layer and applies the
		//container's fade alpha. The field's screen position lives in the render
		//uniforms, so the Sprite itself never moves:
		this._sprite = new (PIXI as any).Sprite(this._renderTexture);
		this._sprite.name = 'shaders-systems-gpu';
		this._sprite.x = 0;
		this._sprite.y = 0;
		(this._sprite as any).interactive = false;
		this._deps.stage.systemsContainer.addChild(this._sprite);
	}
}
